import { Chip } from '../common/Chip'
import type { KursStand } from '../../hooks/instrumentRegeln'
import { zeitKurz, zeitVoll } from '../../hooks/instrumentRegeln'
import { geldtext, kapitalisierung } from '../../pages/priceFormat'
import s from './InstrumentKopf.module.scss'

const laender = new Intl.DisplayNames(['de'], { type: 'region' })

function landName(code: string): string | null {
  if (!code) return null
  try { const n = laender.of(code); return n && n !== code ? n : null } catch { return null }
}

/**
 * Der Kopf der Instrumentseite: links die Kennzeichen als Chips — Branche,
 * Land, Größe, Watchlist —, rechts der Kurs mit Veränderung zum Vortag.
 *
 * Der Name steht in der Kopfzeile der Anwendung, nicht hier: Er stünde
 * sonst zweimal übereinander. ISIN und Kürzel des Hauptplatzes stehen als
 * Zeile darunter, weil man sie zum Weiterarbeiten braucht.
 */
export function InstrumentKopf({ isin, kuerzel, branche, land, groesse, marktkap, marktkapWaehrung,
                                 aufWatchlist, kurs }: {
  isin: string
  /** Das Symbol am Hauptplatz, „XETR:SIE". Leer, wo keins da ist. */
  kuerzel: string | null
  branche: string | null
  /** Der ISIN-Landcode, wird zum Namen. */
  land: string
  groesse: string | null
  marktkap: string | null
  marktkapWaehrung: string | null
  /** Leer heißt: nicht bekannt (etwa ohne Recht), nicht „nein". */
  aufWatchlist: boolean | null
  kurs: KursStand | null
}) {
  const landText = landName(land)
  const kap = marktkap ? kapitalisierung(marktkap, marktkapWaehrung) : null
  return (
    <div className={s.kopf}>
      <div className={s.links}>
        <div className={s.kennung}>{isin}{kuerzel ? ` · ${kuerzel}` : ''}</div>
        <div className={s.chips}>
          {branche && <Chip>{branche}</Chip>}
          {landText && <Chip>{landText}</Chip>}
          {(groesse || kap) && <Chip>{[groesse, kap].filter(Boolean).join(' · ')}</Chip>}
          {aufWatchlist && <Chip tone="good">auf der Watchlist</Chip>}
        </div>
      </div>
      <div className={s.rechts}>
        {kurs ? (
          <>
            <div className={s.kurs}>
              {geldtext(kurs.kurs.price).replace(/ [A-Z]{3}$/, '')}
              <span className={s.waehrung}> {kurs.kurs.price.currency}</span>
            </div>
            <div className={s.aenderung} data-ton={kurs.aenderung?.ton ?? 'dim'}
                 title={zeitVoll(kurs.kurs.observed_at)}>
              {kurs.aenderung
                ? `${vorzeichen(kurs.aenderung.absolut)}${zahl(Math.abs(kurs.aenderung.absolut), 2)} · ${vorzeichen(kurs.aenderung.prozent)}${zahl(Math.abs(kurs.aenderung.prozent), 1)} %`
                : 'ohne Vortag'}
              <span className={s.wann}> · {zeitKurz(kurs.kurs.observed_at)} · {kurs.kurs.exchange}</span>
            </div>
          </>
        ) : (
          <div className={s.ohneKurs}>kein Kurs</div>
        )}
      </div>
    </div>
  )
}

function vorzeichen(n: number): string { return n > 0 ? '+' : n < 0 ? '−' : '' }
function zahl(n: number, stellen: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen })
}
